import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/constants";
import { usePublicSettings } from "@/lib/settings";
import { Zap, Phone, Mail, MapPin, Facebook, Instagram, Globe } from "lucide-react";

// Inline TikTok mark since lucide doesn't ship one.
const TikTok = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.69a8.16 8.16 0 0 0 4.77 1.52V7.78a4.85 4.85 0 0 1-1.84-1.09Z" />
  </svg>
);

const formatAddress = (s) =>
  [s.address_line, s.city, s.region, s.country].filter(Boolean).join(", ");

const formatTel = (raw) => (raw || "").replace(/[^+\d]/g, "");

export const Footer = () => {
  const s = usePublicSettings();
  const socials = [
    { url: s.facebook_url, label: "Facebook", icon: Facebook, testid: "footer-facebook" },
    { url: s.instagram_url, label: "Instagram", icon: Instagram, testid: "footer-instagram" },
    { url: s.tiktok_url, label: "TikTok", icon: TikTok, testid: "footer-tiktok" },
    { url: s.google_maps_url, label: "Google Maps", icon: Globe, testid: "footer-maps" },
  ].filter((x) => !!x.url);

  return (
    <footer className="border-t border-spark-line bg-white mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-8 md:grid-cols-12">
        <div className="md:col-span-4">
          <Link to="/" className="flex items-start gap-3">
            <img src={LOGO_URL} alt={s.business_name} className="h-12 w-12 rounded-full" />
            <div>
              <p className="font-display uppercase tracking-tight text-lg font-bold">{s.business_name}</p>
              <p className="text-sm text-spark-slate">Home workshop · New Zealand</p>
            </div>
          </Link>
          <p className="text-sm text-spark-slate leading-relaxed mt-4">
            Trusted electric scooter, e-bike and mobility scooter repairs serving the Horowhenua region. 21-day labour warranty on every job.
          </p>
          {socials.length > 0 && (
            <div className="flex gap-2 mt-4">
              {socials.map(({ url, label, icon: Icon, testid }) => (
                <a key={label} href={url} target="_blank" rel="noreferrer" aria-label={label} className="size-9 rounded-sm border border-spark-line hover:bg-spark-teal-deep hover:text-spark-yellow hover:border-spark-ink transition flex items-center justify-center" data-testid={testid}>
                  <Icon className="size-4" />
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-3">
          <p className="label-mono mb-3">Get in touch</p>
          <ul className="space-y-2.5 text-sm">
            {s.phone && (
              <li>
                <a href={`tel:${formatTel(s.phone)}`} className="flex items-center gap-2 hover:text-spark-teal" data-testid="footer-phone">
                  <Phone className="size-4 text-spark-teal" /> {s.phone}
                </a>
              </li>
            )}
            {s.email && (
              <li>
                <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:text-spark-teal break-all" data-testid="footer-email">
                  <Mail className="size-4 text-spark-teal shrink-0" /> {s.email}
                </a>
              </li>
            )}
            {s.address_line && (
              <li className="flex items-start gap-2" data-testid="footer-address">
                <MapPin className="size-4 text-spark-teal shrink-0 mt-0.5" />
                <span>{s.address_line}{(s.city || s.region) && <><br/>{[s.city, s.region].filter(Boolean).join(", ")}</>}</span>
              </li>
            )}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="label-mono mb-3">Customer</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="hover:text-spark-teal">Services</Link></li>
            <li><Link to="/book" className="hover:text-spark-teal">Book a Repair</Link></li>
            <li><Link to="/shop" className="hover:text-spark-teal">Shop</Link></li>
            <li><Link to="/track" className="hover:text-spark-teal">Track Your Job</Link></li>
            <li><Link to="/reviews" className="hover:text-spark-teal">Reviews</Link></li>
            <li><Link to="/faq" className="hover:text-spark-teal">Help & FAQ</Link></li>
            <li><Link to="/how-to-pay" className="hover:text-spark-teal">How to Pay</Link></li>
            <li><Link to="/contact" className="hover:text-spark-teal">Contact Us</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="label-mono mb-3">Workshop</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/terms" className="hover:text-spark-teal">Terms & Conditions</Link></li>
            <li><Link to="/admin/login" className="hover:text-spark-teal">Admin Login</Link></li>
          </ul>
          <p className="text-xs text-spark-slate mt-4 leading-relaxed">
            By appointment · drop-offs welcomed.<br/>Mobile service in Horowhenua.
          </p>
        </div>
      </div>
      <div className="border-t border-spark-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-spark-slate">
          <span>© {new Date().getFullYear()} {s.business_name}. All rights reserved.</span>
          <span className="flex items-center gap-1.5">
            <Zap className="size-3.5 text-spark-yellow fill-spark-yellow" /> Sparking life back into your ride.
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
